/**
 * Runs a save a moment after the last change. The timer functions can be
 * replaced, which keeps tests fast and exact.
 */
export class Autosaver {
  #timer: unknown = null;
  #delay: number;
  #save: () => Promise<void>;
  #onError: (error: unknown) => void;
  #setTimer: (fn: () => void, ms: number) => unknown;
  #clearTimer: (id: never) => void;
  #running = Promise.resolve();

  constructor({
    save,
    onError = () => {},
    delay = 800,
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = (id) => clearTimeout(id as unknown as number),
  }: {
    save: () => Promise<void>;
    onError?: (error: unknown) => void;
    delay?: number;
    setTimer?: (fn: () => void, ms: number) => unknown;
    clearTimer?: (id: never) => void;
  }) {
    this.#save = save;
    this.#onError = onError;
    this.#delay = delay;
    this.#setTimer = setTimer;
    this.#clearTimer = clearTimer;
  }

  /** True while a save is waiting for its timer. */
  get pending() {
    return this.#timer !== null;
  }

  /** (Re)starts the countdown; each change pushes the save back. */
  schedule() {
    this.cancel();
    this.#timer = this.#setTimer(() => {
      this.#timer = null;
      this.#run().catch(this.#onError);
    }, this.#delay);
  }

  /** Stops a waiting save without running it. */
  cancel() {
    if (this.#timer !== null) {
      this.#clearTimer(this.#timer as never);
      this.#timer = null;
    }
  }

  /** Saves right now, after any save already running. Errors reach the caller. */
  flush() {
    this.cancel();
    return this.#run();
  }

  #run() {
    const next = this.#running.then(() => this.#save());
    this.#running = next.catch(() => {});
    return next;
  }
}
